import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { GitMerge, Loader2, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ImportRowsTable } from "@/pages/candidates/import/ImportRowsTable";

const FILTERS = [["", "All"], ["new", "New"], ["duplicate", "Duplicate"], ["failed", "Failed"]];

export function ImportPreview({ imp }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["import", imp.id] });
  const call = async (fn, ok) => { setBusy(true); try { await fn(); ok && toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const decide = (row, decision) => call(() => api.post(`/intake/imports/${imp.id}/rows/${row.id}/decision`, { decision }));
  const bulk = (action) => call(() => api.post(`/intake/imports/${imp.id}/bulk-decision`, { action }), action === "skip_duplicates" ? "All duplicates will be skipped" : "All duplicates will be merged");
  const commit = () => call(() => api.post(`/intake/imports/${imp.id}/commit`), "Import started");
  const staging = imp.phase === "staging" || imp.phase === "uploading";
  const pct = imp.total ? Math.round((imp.processed / imp.total) * 100) : 0;
  const rows = imp.rows.filter((r) => !filter || r.status === filter);
  const willImport = imp.rows.filter((r) => r.decision === "import").length;
  const willMerge = imp.rows.filter((r) => r.decision === "merge").length;
  return (
    <div className="space-y-5" data-testid="import-preview">
      <section className="panel space-y-3 p-5" data-testid="import-progress">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <p className="font-medium" data-testid="import-progress-text" aria-live="polite">{imp.processed} of {imp.total} processed{imp.zip_pending > 0 && " · unpacking ZIP…"}</p>
          <p className="text-xs text-ink-secondary">{staging ? "You can leave this page. Processing continues in the background." : "Nothing has been saved yet. Review the rows, then import."}</p>
        </div>
        <Progress value={staging ? pct : 100} className="h-2 bg-brand-tint" data-testid="import-progress-bar" />
        <div className="flex flex-wrap gap-4 text-xs text-ink-secondary">
          <span data-testid="import-count-new"><strong className="text-success">{imp.counts.new}</strong> New</span>
          <span data-testid="import-count-duplicate"><strong className="text-caution">{imp.counts.duplicate}</strong> Duplicate</span>
          <span data-testid="import-count-failed"><strong className="text-risk">{imp.counts.failed}</strong> Failed</span>
        </div>
      </section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-[5px] border border-line bg-white p-0.5" role="tablist" aria-label="Filter rows by status">
          {FILTERS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} data-testid={`import-filter-${k || "all"}`}
              className={cn("rounded-md px-3 py-1 text-sm transition-colors duration-150", filter === k ? "bg-brand-tint font-medium text-brand" : "text-ink-secondary hover:text-ink")}>
              {l}{k && ` (${imp.counts[k]})`}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy || staging || !imp.counts.duplicate} onClick={() => bulk("skip_duplicates")} data-testid="import-skip-all-dups"><SkipForward size={14} aria-hidden="true" /> Skip all duplicates</Button>
          <Button variant="outline" size="sm" disabled={busy || staging || !imp.counts.duplicate} onClick={() => bulk("merge_duplicates")} data-testid="import-merge-all-dups"><GitMerge size={14} aria-hidden="true" /> Merge all duplicates</Button>
        </div>
      </div>
      <ImportRowsTable rows={rows} onDecide={decide} busy={busy || staging} />
      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-3 rounded-[6px] border border-line bg-white/95 p-3 shadow-sm">
        <p className="text-sm text-ink-secondary" data-testid="import-commit-hint">{willImport} to import · {willMerge} to merge · {imp.total - willImport - willMerge} won't be added</p>
        <Button onClick={commit} disabled={busy || staging || !(willImport + willMerge)} data-testid="import-commit-btn">{busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} Import {willImport + willMerge} candidate(s)</Button>
      </div>
    </div>
  );
}
